import React from 'react';
import { Link } from 'react-router-dom';
import {
  Settings as SettingsIcon,
  Users,
  Mail,
  FileText,
  Clock,
  BookOpen,
  ChevronRight,
} from 'lucide-react';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

/**
 * Admin Settings Page
 *
 * A directory of the administration areas that exist in the application.
 * Each entry links to a real route defined in AppRouter.tsx.
 */
const adminAreas = [
  {
    to: '/admin/users',
    name: 'Users',
    description: 'Manage user accounts and roles.',
    icon: Users,
  },
  {
    to: '/admin/email/settings',
    name: 'Email Settings',
    description: 'Inspect email configuration, notification history and logs.',
    icon: Mail,
  },
  {
    to: '/sla/policies',
    name: 'SLA Policies',
    description: 'Set response and resolution targets for tickets.',
    icon: FileText,
  },
  {
    to: '/sla/business-hours',
    name: 'Business Hours',
    description: 'Define the working calendar used for SLA calculations.',
    icon: Clock,
  },
  {
    to: '/knowledge-base/admin',
    name: 'Knowledge Base',
    description: 'Create and maintain knowledge base articles.',
    icon: BookOpen,
  },
];

export function Settings() {
  useDocumentTitle('System Settings | SupportDesk');

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white shadow">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="py-6 md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center">
                <SettingsIcon className="w-8 h-8 text-gray-400 mr-3" aria-hidden="true" />
                <div>
                  <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">
                    System Settings
                  </h1>
                  <p className="mt-1 text-sm text-gray-500">
                    Administration areas for configuring SupportDesk
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Administration Directory */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <nav aria-label="Administration areas">
          <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {adminAreas.map(({ to, name, description, icon: Icon }) => (
              <li key={to} className="h-full">
                <Link
                  to={to}
                  className="group flex h-full flex-col justify-between rounded-xl border border-gray-200 bg-white p-6 shadow-soft transition-all duration-200 hover:shadow-large hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:border-gray-700 dark:bg-gray-800"
                >
                  <div className="flex items-start">
                    <Icon className="w-6 h-6 text-gray-400 mr-3 flex-shrink-0" aria-hidden="true" />
                    <div className="min-w-0">
                      <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                        {name}
                      </h2>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {description}
                      </p>
                    </div>
                  </div>
                  <span className="mt-4 inline-flex items-center text-sm font-medium text-blue-600 dark:text-blue-400">
                    Manage
                    <ChevronRight
                      className="w-4 h-4 ml-1 transition-transform duration-200 group-hover:translate-x-0.5"
                      aria-hidden="true"
                    />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>
  );
}
